import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Stack,
  Typography,
} from "@mui/material";
import { UnconfiguredRules } from "system/types/interfaces";

interface AddConfigRuleModalProps {
  open: boolean;
  onClose: () => void;
  onAddRule: (data: {
    rule: string;
    applicability: string;
    value: string;
    description: string;
    type: string;
    regex?: string;
    valueShouldBeLogged?: boolean;
  }) => void;
  unConfiguredList: UnconfiguredRules[];

  initialItem?: UnconfiguredRules | null;
  showCommands?: boolean;
}

export const AddConfigRuleModal: React.FC<AddConfigRuleModalProps> = ({
  open,
  onClose,
  onAddRule,
  unConfiguredList,
  initialItem,
  showCommands = true,
}) => {
  const [ruleType, setRuleType] = useState<string>("");
  const [ruleValue, setRuleValue] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [selectedRule, setSelectedRule] = useState<UnconfiguredRules | null>(null);

  const [valueError, setValueError] = useState<string>("");

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    setValueError("");

    const selectedName = event.target.value;
    setRuleType(selectedName);

    if (showCommands && (selectedName === "Pre-run Command" || selectedName === "Post-run Command")) {
      setSelectedRule(null);
      setDescription("");
      setRuleValue("");
      return;
    }

    const found = unConfiguredList.find((u) => u.name === selectedName);
    if (found) {
      setSelectedRule(found);
      setDescription(found.description || "");
      setRuleValue(found.defaultValue || "");
    } else {
      setSelectedRule(null);
      setDescription("");
      setRuleValue("");
    }
  };

  const clearModal = () => {
    setSelectedRule(null);
    setRuleType("");
    setDescription("");
    setRuleValue("");
    setValueError("");
  };

  const handleAdd = () => {
    if (!ruleType) return;

    if (
      !ruleValue &&
      ((showCommands && (ruleType === "Pre-run Command" || ruleType === "Post-run Command")) || selectedRule?.type === "string")
    ) {
      setValueError("Please enter a valid value");
      return;
    }

    if (selectedRule?.type === "string" && selectedRule?.regex) {
      const pattern = `^${selectedRule.regex}$`;
      const reg = new RegExp(pattern, "i");

      if (!reg.test(ruleValue)) {
        setValueError(`The provided value does not match the expected value pattern \n ${selectedRule.regex}`);
        return;
      }
    }

    let ruleString = "";
    if (showCommands && ruleType === "Pre-run Command") {
      ruleString = `Pre-run Command`;
    } else if (showCommands && ruleType === "Post-run Command") {
      ruleString = `Post-run Command`;
    } else {
      ruleString = selectedRule?.name || "";
    }

    onAddRule({
      rule: ruleString,
      applicability: "",
      value: ruleValue,
      description,
      type: selectedRule?.type || (showCommands && (ruleType === "Pre-run Command" || ruleType === "Post-run Command") ? "string" : ""),
      regex: selectedRule?.regex,
      valueShouldBeLogged: selectedRule?.valueShouldBeLogged,
    });

    clearModal();
  };

  const renderValueField = () => {
    if (showCommands && (ruleType === "Pre-run Command" || ruleType === "Post-run Command")) {
      return (
        <TextField
          fullWidth
          label="Value"
          multiline
          rows={3}
          sx={{ color: "#fff" }}
          value={ruleValue}
          onChange={(e) => {
            setValueError("");
            setRuleValue(e.target.value);
          }}
          inputProps={{ style: { color: "#fff" } }}
          placeholder="Enter your script/command"
          error={!!valueError}
          helperText={valueError || ""}
          FormHelperTextProps={{
            sx: { whiteSpace: "pre-line" },
          }}
        />
      );
    }

    if (!selectedRule) {
      return null;
    }

    if (selectedRule.type === "bool") {
      return (
        <FormControlLabel
          label="Value"
          labelPlacement="top"
          sx={{ color: "#fff", alignItems: "flex-start" }}
          control={
            <Stack direction="row" spacing={0} sx={{ alignItems: "center" }}>
              <Typography sx={{ fontSize: "14px", opacity: ruleValue === "false" ? 1 : 0.4 }}>False</Typography>
              <Switch checked={ruleValue === "true"} onChange={(e) => setRuleValue(e.target.checked ? "true" : "false")} />
              <Typography sx={{ fontSize: "14px", opacity: ruleValue === "true" ? 1 : 0.4 }}>True</Typography>
            </Stack>
          }
        />
      );
    }

    if (selectedRule.type === "enum") {
      const enumOptions = selectedRule.regex?.replace(/[()]/g, "").split("|") || [];

      return (
        <FormControl fullWidth variant="outlined">
          <InputLabel sx={{ color: "#fff" }}>Value</InputLabel>
          <Select label="Value" value={ruleValue} onChange={(e) => setRuleValue(e.target.value as string)} sx={{ color: "#fff" }}>
            {enumOptions.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        label="Value"
        multiline
        rows={3}
        sx={{ color: "#fff" }}
        inputProps={{ style: { color: "#fff" } }}
        value={ruleValue}
        onChange={(e) => {
          setValueError("");
          setRuleValue(e.target.value);
        }}
        placeholder="Enter string value"
        error={!!valueError}
        helperText={valueError || ""}
        FormHelperTextProps={{
          sx: { whiteSpace: "pre-line" },
        }}
      />
    );
  };

  useEffect(() => {
    if (initialItem) {
      setSelectedRule(initialItem);
      setRuleType(initialItem.name);
      setDescription(initialItem.description ?? "");
      setRuleValue(initialItem.defaultValue ?? "");
    } else {
      clearModal();
    }
  }, [initialItem]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ backgroundColor: "#1b1a19", color: "#fff" }}>Add Configuration Rule</DialogTitle>

      <DialogContent sx={{ backgroundColor: "#1b1a19", display: "flex", flexDirection: "column", gap: 3, alignItems: "flex-start" }}>
        <FormControl fullWidth variant="outlined" sx={{ color: "#fff", mt: 2 }}>
          <InputLabel id="configRule-label" sx={{ color: "#fff" }}>
            Configuration Rule
          </InputLabel>
          <Select
            labelId="configRule-label"
            id="configRule-select"
            label="Configuration Rule"
            value={ruleType}
            onChange={handleSelectChange}
            sx={{ color: "#fff" }}
          >
            {showCommands && [
              <MenuItem key="pre-run" value="Pre-run Command">
                Pre-Run Command
              </MenuItem>,
              <MenuItem key="post-run" value="Post-run Command">
                Post-Run Command
              </MenuItem>,
            ]}
            {unConfiguredList.length > 0 && [
              ...(showCommands
                ? [
                    <MenuItem key="separator" disabled>
                      ──────────
                    </MenuItem>,
                  ]
                : []),
              ...unConfiguredList.map((item) => (
                <MenuItem key={item.name} value={item.name}>
                  {item.name}
                </MenuItem>
              )),
            ]}
            {(!showCommands || unConfiguredList.length === 0) && <MenuItem disabled>No rules available</MenuItem>}
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Description"
          multiline
          value={description}
          hidden={(showCommands && (ruleType === "Pre-run Command" || ruleType === "Post-run Command")) || !selectedRule?.name}
          inputProps={{ readOnly: true, style: { color: "#fff" } }}
          sx={{ color: "#fff" }}
          placeholder="Enter optional description"
        />

        {renderValueField()}
      </DialogContent>

      <DialogActions sx={{ backgroundColor: "#1b1a19" }}>
        <Button
          variant="outlined"
          onClick={() => {
            onClose();
            clearModal();
          }}
        >
          Cancel
        </Button>
        <Button variant="contained" onClick={handleAdd}>
          Add Rule
        </Button>
      </DialogActions>
    </Dialog>
  );
};
